import React, { useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { api } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { CalendarBlank, CaretLeft, CaretRight, Plus, Trash, Megaphone } from "@phosphor-icons/react";
import { toast } from "sonner";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const DOT = { emerald: "bg-emerald-500", amber: "bg-amber-500", blue: "bg-blue-500", purple: "bg-purple-500" };
const CARD = { emerald: "border-emerald-100 bg-emerald-50/40", amber: "border-amber-200 bg-amber-50/50", blue: "border-blue-200 bg-blue-50/50", purple: "border-purple-200 bg-purple-50/50" };

export const CalendarPanel = ({ canPublish = false }) => {
  const { t, lang } = useI18n();
  const locale = lang === "es" ? "es-ES" : "en-US";
  const today = new Date();
  const [cur, setCur] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [publish, setPublish] = useState(false);
  const [capacity, setCapacity] = useState("");
  const [busy, setBusy] = useState(false);

  const monthKey = `${cur.y}-${pad(cur.m + 1)}`;

  const reqRef = useRef(0);
  const load = () => {
    const rid = ++reqRef.current;
    api.get(`/me/calendar?month=${monthKey}`)
      .then((r) => { if (rid === reqRef.current) setEvents(r.data); })
      .catch(() => { if (rid === reqRef.current) setEvents([]); });
  };
  useEffect(() => { load(); }, [monthKey]); // eslint-disable-line

  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [events]);

  const firstDow = new Date(cur.y, cur.m, 1).getDay();
  const daysInMonth = new Date(cur.y, cur.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(locale, { weekday: "short" }).format(new Date(2023, 0, 1 + i))),
    [locale]
  );
  const monthName = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(new Date(cur.y, cur.m, 1));

  const prev = () => setCur((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  const next = () => setCur((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));
  const goToday = () => setCur({ y: today.getFullYear(), m: today.getMonth() });

  const openDay = (d) => { setSelected(ymd(cur.y, cur.m, d)); setTitle(""); setNote(""); setPublish(false); setCapacity(""); };

  const add = async () => {
    if (!title.trim()) { toast.error(t("calendar_title_required")); return; }
    setBusy(true);
    try {
      if (publish && canPublish) {
        await api.post("/admin/coop-events", { date: selected, title, note, capacity: Number(capacity || 0) });
      } else {
        await api.post("/me/calendar", { date: selected, title, note });
      }
      setTitle(""); setNote(""); setPublish(false); setCapacity("");
      load();
      toast.success(t("saved"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const rsvp = async (ev) => {
    try { await api.post(`/me/coop-events/${ev.id}/rsvp`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (ev) => {
    try {
      if (ev.kind === "official") await api.delete(`/admin/coop-events/${ev.id}`);
      else await api.delete(`/me/calendar/${ev.id}`);
      load();
    } catch (e) { toast.error("Error"); }
  };

  const canDelete = (ev) => ev.kind === "personal" || (ev.kind === "official" && canPublish);
  const tagFor = (ev) => (ev.kind === "official" ? t("calendar_official") : ev.auto ? t("calendar_auto_event") : null);

  const [attById, setAttById] = useState({});
  const loadAttendees = async (eid) => {
    try { const { data } = await api.get(`/admin/coop-events/${eid}/attendees`); setAttById((p) => ({ ...p, [eid]: data.attendees })); }
    catch (e) { toast.error("Error"); }
  };
  const downloadAtt = async (eid, fmt) => {
    try {
      const res = await api.get(`/admin/coop-events/${eid}/attendees.${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `asistentes.${fmt}`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  const todayStr = ymd(today.getFullYear(), today.getMonth(), today.getDate());
  const selEvents = selected ? (byDate[selected] || []) : [];
  const selLabel = selected
    ? new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date(selected + "T00:00:00"))
    : "";

  return (
    <div data-testid="calendar-panel">
      <div className="flex items-center gap-2 mb-6">
        <CalendarBlank size={26} weight="duotone" className="text-emerald-700" />
        <div>
          <h2 className="font-display text-2xl text-emerald-900">{t("calendar_title")}</h2>
          <p className="text-sm text-slate-500">{t("calendar_desc")}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-3 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> {t("calendar_legend_personal")}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> {t("calendar_legend_loan")}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> {t("calendar_legend_term")}</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-purple-500" /> {t("calendar_legend_official")}</span>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-6 max-w-3xl">
        <div className="flex items-center justify-between mb-4">
          <button type="button" onClick={prev} data-testid="cal-prev" className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center text-emerald-800 hover:bg-emerald-50">
            <CaretLeft size={18} weight="bold" />
          </button>
          <div className="text-center">
            <p className="font-display text-lg font-bold text-emerald-900 capitalize" data-testid="cal-month-label">{monthName}</p>
            <button type="button" onClick={goToday} data-testid="cal-today" className="text-xs font-semibold text-emerald-600 hover:text-emerald-800">{t("calendar_today")}</button>
          </div>
          <button type="button" onClick={next} data-testid="cal-next" className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center text-emerald-800 hover:bg-emerald-50">
            <CaretRight size={18} weight="bold" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {weekdays.map((w) => (
            <div key={w} className="text-center text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-slate-400 py-1 capitalize">{w}</div>
          ))}
          {cells.map((d, i) => {
            if (d === null) return <div key={`e-${i}`} />;
            const ds = ymd(cur.y, cur.m, d);
            const dayEvents = byDate[ds] || [];
            const isToday = ds === todayStr;
            return (
              <button
                key={ds}
                type="button"
                onClick={() => openDay(d)}
                data-testid={`cal-day-${ds}`}
                className={`aspect-square rounded-lg border p-1 flex flex-col items-center justify-start text-sm transition-colors ${isToday ? "border-emerald-500 bg-emerald-50" : "border-gray-100 hover:bg-slate-50"}`}
              >
                <span className={`font-mono-num ${isToday ? "font-bold text-emerald-800" : "text-slate-700"}`}>{d}</span>
                {dayEvents.length > 0 && (
                  <span className="mt-auto mb-0.5 inline-flex items-center gap-0.5">
                    {dayEvents.slice(0, 3).map((ev) => (
                      <span key={ev.id} className={`w-1.5 h-1.5 rounded-full ${DOT[ev.color] || "bg-emerald-500"}`} />
                    ))}
                    {dayEvents.length > 3 && <span className="text-[9px] text-emerald-600">+</span>}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent data-testid="cal-day-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">{selLabel}</DialogTitle>
            <DialogDescription>{t("calendar_events_on")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-52 overflow-y-auto">
            {selEvents.length === 0 ? (
              <p className="text-sm text-slate-400 py-3 text-center" data-testid="cal-no-events">{t("calendar_no_events")}</p>
            ) : (
              selEvents.map((ev) => (
                <div key={ev.id} className={`flex items-start justify-between gap-2 rounded-lg border p-3 ${CARD[ev.color] || CARD.emerald}`} data-testid={`cal-event-${ev.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-emerald-900 text-sm truncate">{ev.title}</p>
                    {ev.note ? <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{ev.note}</p> : null}
                    {tagFor(ev) ? <span className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{tagFor(ev)}</span> : null}
                    {ev.kind === "official" && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {ev.capacity > 0 ? (
                          <span className="text-[10px] font-semibold text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full" data-testid={`cal-capacity-${ev.id}`}>{ev.attendee_count}/{ev.capacity} · {Math.max(0, ev.capacity - ev.attendee_count)} {t("spots_left")}</span>
                        ) : (ev.attendee_count > 0 ? <span className="text-[10px] text-slate-500">{ev.attendee_count} {t("attendees")}</span> : null)}
                        {ev.is_attending ? (
                          <button data-testid={`cal-rsvp-${ev.id}`} onClick={() => rsvp(ev)} className="text-[11px] font-semibold text-emerald-700 border border-emerald-300 rounded-full px-2.5 py-1 hover:bg-emerald-50">✓ {t("attending")} · {t("cancel_attend")}</button>
                        ) : (
                          <button data-testid={`cal-rsvp-${ev.id}`} onClick={() => rsvp(ev)} disabled={ev.capacity > 0 && ev.attendee_count >= ev.capacity} className="text-[11px] font-semibold text-white bg-emerald-700 rounded-full px-3 py-1 hover:bg-emerald-800 disabled:opacity-40">{ev.capacity > 0 && ev.attendee_count >= ev.capacity ? t("event_full") : t("attend")}</button>
                        )}
                        {canPublish && (
                          <>
                            <button data-testid={`cal-att-${ev.id}`} onClick={() => loadAttendees(ev.id)} className="text-[11px] font-semibold text-purple-700 border border-purple-300 rounded-full px-2.5 py-1 hover:bg-purple-50">{t("view_attendees")}</button>
                            <button data-testid={`cal-att-xlsx-${ev.id}`} onClick={() => downloadAtt(ev.id, "xlsx")} className="text-[11px] font-semibold text-emerald-700 border border-emerald-300 rounded-full px-2.5 py-1 hover:bg-emerald-50">Excel</button>
                            <button data-testid={`cal-att-pdf-${ev.id}`} onClick={() => downloadAtt(ev.id, "pdf")} className="text-[11px] font-semibold text-red-700 border border-red-200 rounded-full px-2.5 py-1 hover:bg-red-50">PDF</button>
                          </>
                        )}
                      </div>
                    )}
                    {canPublish && ev.kind === "official" && attById[ev.id] && (
                      <div className="mt-1.5 text-[11px] text-slate-600 space-y-0.5" data-testid={`cal-att-list-${ev.id}`}>
                        {attById[ev.id].length === 0 ? <span className="text-slate-400">{t("no_attendees")}</span> : attById[ev.id].map((a, k) => (
                          <div key={k}>• {a.user_name}{a.email ? ` · ${a.email}` : a.phone ? ` · ${a.phone}` : ""}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  {canDelete(ev) && (
                    <button type="button" onClick={() => remove(ev)} data-testid={`cal-event-del-${ev.id}`} className="text-slate-400 hover:text-red-600 flex-shrink-0" title={t("delete")} aria-label={t("delete")}>
                      <Trash size={16} weight="bold" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="pt-2 border-t border-gray-100 space-y-3">
            <div>
              <Label className="text-emerald-900">{t("calendar_event")}</Label>
              <Input data-testid="cal-event-title" value={title} onChange={(e) => setTitle(e.target.value)} className="h-11 mt-1.5" placeholder={t("calendar_event")} />
            </div>
            <div>
              <Label className="text-emerald-900">{t("calendar_note")}</Label>
              <Textarea data-testid="cal-event-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" rows={2} placeholder={t("calendar_note_ph")} />
            </div>
            {canPublish && (
              <div className="flex items-center justify-between rounded-lg border border-purple-100 bg-purple-50/50 p-3">
                <Label className="text-purple-900 flex items-center gap-2"><Megaphone size={18} weight="duotone" /> {t("calendar_publish_all")}</Label>
                <Switch data-testid="cal-publish-switch" checked={publish} onCheckedChange={setPublish} />
              </div>
            )}
            {canPublish && publish && (
              <div>
                <Label className="text-emerald-900">{t("event_capacity")}</Label>
                <Input data-testid="cal-event-capacity" type="number" min="0" value={capacity} onChange={(e) => setCapacity(e.target.value)} className="h-11 mt-1.5" placeholder="0 = ∞" />
              </div>
            )}
            <Button data-testid="cal-add-event" onClick={add} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 w-full">
              <Plus size={16} weight="bold" className="mr-1" /> {t("calendar_add")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
